import { useEffect, useMemo, useState } from "react";
import { SelectInput, useEntityList } from "@pericles/framework-frontend";

import {
  exportRoadmapSuggestionPdf,
  exportRoadmapSuggestionZip,
  fetchRoadmapProjects,
  fetchRoadmapSuggestion,
  generateRoadmapSuggestion,
  isMigrationsRequiredError,
  updateRoadmapSuggestion,
  type RoadmapProject,
  type RoadmapSuggestionParams,
  type RoadmapSuggestionRecord,
  type RoadmapSuggestionWave,
  type RoadmapMigrationsRequiredDetails,
} from "../api/roadmapClient";

import { Badge, EmptyState, Pane, PaneHeader, Section, Skeleton, Tag } from "./roadmap/DesignSystem";
import { AtlasMigrationsRequiredCallout } from "./roadmap/AtlasMigrationsRequiredCallout";

type CustomerListItem = { id: string | number; name?: string };

type WaveApplicationRow = {
  segmentId: string;
  name: string | null;
  finalScore: number | null;
  prerequisites: string[];
  rationale: string;
  waveId: string;
  waveName: string;
  waveStart: string;
  waveEnd: string;
};

const DEFAULT_PARAMS: RoadmapSuggestionParams = {
  startDate: new Date().toISOString().slice(0, 10),
  waveDurationWeeks: 12,
  horizonMonths: 6,
  maxParallelApps: 3,
  capacityAssumptions: "",
};

function formatScore(score: number | null): string {
  if (score == null || Number.isNaN(score)) return "—";
  return score.toFixed(2);
}

function sanitizeParams(params: RoadmapSuggestionParams): RoadmapSuggestionParams {
  return {
    ...params,
    capacityAssumptions: params.capacityAssumptions?.trim() ? params.capacityAssumptions : null,
  };
}

function flattenWaveApplications(waves: RoadmapSuggestionWave[]): WaveApplicationRow[] {
  return waves.flatMap((wave) =>
    wave.applications.map((app) => ({
      segmentId: app.segmentId,
      name: app.name ?? null,
      finalScore: app.finalScore ?? null,
      prerequisites: app.prerequisites ?? [],
      rationale: app.rationale,
      waveId: wave.id,
      waveName: wave.name,
      waveStart: wave.start,
      waveEnd: wave.end,
    })),
  );
}

export function AiRoadmapRoadmapSuggestionScreen(): JSX.Element {
  const [projects, setProjects] = useState<RoadmapProject[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>(() => {
    return localStorage.getItem("studio.aiRoadmap.activeCustomer") ?? "";
  });
  const [selectedProjectId, setSelectedProjectId] = useState<string>(() => {
    return localStorage.getItem("studio.aiRoadmap.activeProject") ?? "";
  });
  const [suggestion, setSuggestion] = useState<RoadmapSuggestionRecord | null>(null);
  const [draftSuggestion, setDraftSuggestion] = useState<RoadmapSuggestionRecord | null>(null);
  const [paramsInput, setParamsInput] = useState<RoadmapSuggestionParams>(DEFAULT_PARAMS);
  const [loading, setLoading] = useState(false);
  const [dataError, setDataError] = useState<string | null>(null);
  const [generateState, setGenerateState] = useState<"idle" | "loading">("idle");
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">("idle");
  const [exportPdfState, setExportPdfState] = useState<"idle" | "loading">("idle");
  const [exportZipState, setExportZipState] = useState<"idle" | "loading">("idle");
  const [editDirty, setEditDirty] = useState(false);
  const [migrationError, setMigrationError] = useState<RoadmapMigrationsRequiredDetails | null>(null);

  const { data: customerOptionsData } = useEntityList<CustomerListItem>({
    entity: "Customer",
    page: 0,
    pageSize: 1000,
  });

  const customerOptions = useMemo(() => {
    const options = (customerOptionsData ?? []).map((customer) => ({
      value: String(customer.id),
      label: customer.name ?? String(customer.id),
    }));
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [customerOptionsData]);

  const projectOptions = useMemo(() => {
    return projects.map((project) => ({ value: project.id, label: project.name }));
  }, [projects]);

  const selectedProject = useMemo(
    () => projects.find((project) => project.id === selectedProjectId) ?? null,
    [projects, selectedProjectId],
  );

  const canEdit = selectedProject?.role === "ADMIN" || selectedProject?.role === "EDITOR";

  useEffect(() => {
    if (!selectedCustomerId) {
      setProjects([]);
      setSelectedProjectId("");
      return;
    }
    localStorage.setItem("studio.aiRoadmap.activeCustomer", selectedCustomerId);
    let isActive = true;
    fetchRoadmapProjects(selectedCustomerId)
      .then((loaded) => {
        if (!isActive) return;
        setProjects(loaded);
        if (!loaded.length) {
          setSelectedProjectId("");
          return;
        }
        if (!loaded.find((project) => project.id === selectedProjectId)) {
          setSelectedProjectId(loaded[0].id);
        }
      })
      .catch(() => {
        if (!isActive) return;
        setProjects([]);
        setSelectedProjectId("");
      });
    return () => {
      isActive = false;
    };
  }, [selectedCustomerId, selectedProjectId]);

  useEffect(() => {
    if (!selectedProjectId) {
      setSuggestion(null);
      setDraftSuggestion(null);
      setParamsInput(DEFAULT_PARAMS);
      setEditDirty(false);
      return;
    }
    localStorage.setItem("studio.aiRoadmap.activeProject", selectedProjectId);
    let isActive = true;
    setLoading(true);
    setDataError(null);
    setMigrationError(null);
    fetchRoadmapSuggestion(selectedProjectId)
      .then((payload) => {
        if (!isActive) return;
        setSuggestion(payload.suggestion);
        setDraftSuggestion(payload.suggestion);
        setEditDirty(false);
        if (payload.suggestion?.paramsJson) {
          setParamsInput({
            ...payload.suggestion.paramsJson,
            capacityAssumptions: payload.suggestion.paramsJson.capacityAssumptions ?? "",
          });
        } else {
          setParamsInput(DEFAULT_PARAMS);
        }
      })
      .catch((error) => {
        if (!isActive) return;
        if (isMigrationsRequiredError(error)) {
          setMigrationError(error.details);
          setDataError(null);
          setSuggestion(null);
          setDraftSuggestion(null);
          return;
        }
        setDataError(error instanceof Error ? error.message : "Impossible de charger la suggestion.");
        setSuggestion(null);
        setDraftSuggestion(null);
      })
      .finally(() => {
        if (!isActive) return;
        setLoading(false);
      });
    return () => {
      isActive = false;
    };
  }, [selectedProjectId]);

  const waves = draftSuggestion?.suggestionJson.waves ?? [];
  const flatApplications = useMemo(() => flattenWaveApplications(waves), [waves]);
  const applicationNameById = useMemo(() => {
    const map = new Map<string, string>();
    flatApplications.forEach((app) => {
      map.set(app.segmentId, app.name ?? app.segmentId);
    });
    return map;
  }, [flatApplications]);

  const handleGenerate = async () => {
    if (!selectedProjectId) return;
    setGenerateState("loading");
    setDataError(null);
    setMigrationError(null);
    try {
      const payload = await generateRoadmapSuggestion(selectedProjectId, sanitizeParams(paramsInput));
      setSuggestion(payload.suggestion);
      setDraftSuggestion(payload.suggestion);
      setEditDirty(false);
      if (payload.suggestion?.paramsJson) {
        setParamsInput({
          ...payload.suggestion.paramsJson,
          capacityAssumptions: payload.suggestion.paramsJson.capacityAssumptions ?? "",
        });
      }
    } catch (error) {
      if (isMigrationsRequiredError(error)) {
        setMigrationError(error.details);
        setDataError(null);
      } else {
        setDataError(error instanceof Error ? error.message : "Impossible de générer la suggestion.");
      }
    } finally {
      setGenerateState("idle");
    }
  };

  const handleSaveEdits = async () => {
    if (!selectedProjectId || !draftSuggestion) return;
    setSaveState("saving");
    setDataError(null);
    try {
      const payload = await updateRoadmapSuggestion(selectedProjectId, {
        suggestionJson: draftSuggestion.suggestionJson,
        paramsJson: sanitizeParams(paramsInput),
        changeSummary: "Manual roadmap edit",
      });
      setSuggestion(payload.suggestion);
      setDraftSuggestion(payload.suggestion);
      setEditDirty(false);
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 1500);
    } catch (error) {
      if (isMigrationsRequiredError(error)) {
        setMigrationError(error.details);
        setDataError(null);
      } else {
        setDataError(error instanceof Error ? error.message : "Impossible de sauvegarder la suggestion.");
      }
      setSaveState("idle");
    }
  };

  const handleWaveDateChange = (waveId: string, field: "start" | "end", value: string) => {
    setDraftSuggestion((current) => {
      if (!current) return current;
      const updatedWaves = current.suggestionJson.waves.map((wave) =>
        wave.id === waveId ? { ...wave, [field]: value } : wave,
      );
      setEditDirty(true);
      return { ...current, suggestionJson: { ...current.suggestionJson, waves: updatedWaves } };
    });
  };

  const handleMoveApplication = (segmentId: string, targetWaveId: string) => {
    setDraftSuggestion((current) => {
      if (!current) return current;
      let movedApp: RoadmapSuggestionWave["applications"][number] | null = null;
      const updatedWaves = current.suggestionJson.waves.map((wave) => {
        if (wave.applications.some((app) => app.segmentId === segmentId)) {
          const remaining = wave.applications.filter((app) => {
            if (app.segmentId === segmentId) {
              movedApp = app;
              return false;
            }
            return true;
          });
          return { ...wave, applications: remaining };
        }
        return wave;
      });
      if (movedApp) {
        const targetIndex = updatedWaves.findIndex((wave) => wave.id === targetWaveId);
        if (targetIndex >= 0) {
          updatedWaves[targetIndex] = {
            ...updatedWaves[targetIndex],
            applications: [...updatedWaves[targetIndex].applications, movedApp],
          };
        }
      }
      setEditDirty(true);
      return { ...current, suggestionJson: { ...current.suggestionJson, waves: updatedWaves } };
    });
  };

  const handleExportPdf = async () => {
    if (!selectedProjectId) return;
    setExportPdfState("loading");
    try {
      const blob = await exportRoadmapSuggestionPdf(selectedProjectId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `roadmap-suggestion-${selectedProjectId}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (isMigrationsRequiredError(error)) {
        setMigrationError(error.details);
      } else {
        console.error(error);
      }
    } finally {
      setExportPdfState("idle");
    }
  };

  const handleExportZip = async () => {
    if (!selectedProjectId) return;
    setExportZipState("loading");
    try {
      const blob = await exportRoadmapSuggestionZip(selectedProjectId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `roadmap-suggestion-${selectedProjectId}.zip`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (isMigrationsRequiredError(error)) {
        setMigrationError(error.details);
      } else {
        console.error(error);
      }
    } finally {
      setExportZipState("idle");
    }
  };

  const handleDownloadJson = () => {
    if (!draftSuggestion) return;
    const payload = {
      inputsHash: draftSuggestion.inputsHash,
      stopReason: draftSuggestion.stopReason,
      revision: draftSuggestion.revision,
      params: sanitizeParams(paramsInput),
      suggestion: draftSuggestion.suggestionJson,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `roadmap-suggestion-${draftSuggestion.projectId}.json`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const stopReason = draftSuggestion?.stopReason ?? "NO_OP";
  const missingInfo = draftSuggestion?.suggestionJson.missingInfo ?? [];
  const exportsDisabled = stopReason !== "PASS";

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[220px]">
          <div className="text-xs font-semibold uppercase text-muted-foreground">Client</div>
          <SelectInput value={selectedCustomerId} onChange={(event) => setSelectedCustomerId(event.target.value)}>
            <option value="">Sélectionner</option>
            {customerOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </SelectInput>
        </div>
        <div className="min-w-[260px]">
          <div className="text-xs font-semibold uppercase text-muted-foreground">Projet</div>
          <SelectInput
            value={selectedProjectId}
            onChange={(event) => setSelectedProjectId(event.target.value)}
            disabled={!selectedCustomerId}
          >
            <option value="">Sélectionner</option>
            {projectOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </SelectInput>
        </div>
      </div>

      <Pane className="overflow-hidden">
        <PaneHeader
          title="Roadmap suggestion"
          meta={
            draftSuggestion ? (
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <Badge label={draftSuggestion.status} />
                <Badge label={stopReason} tone={stopReason === "PASS" ? "success" : "warning"} />
                <span>Revision {draftSuggestion.revision}</span>
                {draftSuggestion.inputsHash ? <span>Hash {draftSuggestion.inputsHash.slice(0, 8)}…</span> : null}
              </div>
            ) : null
          }
          actions={
            selectedProjectId ? (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={!canEdit || generateState === "loading"}
                  className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {generateState === "loading" ? "Génération…" : "Générer"}
                </button>
                <button
                  type="button"
                  onClick={handleExportPdf}
                  disabled={exportsDisabled || exportPdfState === "loading"}
                  className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {exportPdfState === "loading" ? "Export PDF…" : "Export PDF"}
                </button>
                <button
                  type="button"
                  onClick={handleExportZip}
                  disabled={exportsDisabled || exportZipState === "loading"}
                  className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {exportZipState === "loading" ? "Export ZIP…" : "Export ZIP"}
                </button>
                <button
                  type="button"
                  onClick={handleDownloadJson}
                  disabled={!draftSuggestion}
                  className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Télécharger JSON
                </button>
              </div>
            ) : null
          }
        />

        {loading ? (
          <Skeleton rows={6} />
        ) : migrationError ? (
          <div className="space-y-4 p-4">
            <AtlasMigrationsRequiredCallout details={migrationError} />
            <EmptyState
              title="Migrations nécessaires"
              description="Appliquez les migrations Prisma pour générer la suggestion roadmap."
            />
          </div>
        ) : dataError ? (
          <EmptyState title={dataError} description="Réessayez ou changez de projet." />
        ) : !selectedProjectId ? (
          <EmptyState title="Sélectionnez un projet" description="Choisissez un projet Atlas pour lancer la suggestion." />
        ) : !draftSuggestion ? (
          <EmptyState
            title="Aucune suggestion" 
            description="Générez la suggestion pour structurer les vagues et hypothèses."
            action={
              <button
                type="button"
                onClick={handleGenerate}
                disabled={!canEdit || generateState === "loading"}
                className="button button-primary"
              >
                Générer la suggestion
              </button>
            }
          />
        ) : (
          <div className="space-y-4">
            {stopReason === "NEEDS_APPROVAL" ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-700">
                Validation requise avant export. Vérifiez les informations manquantes ci-dessous.
              </div>
            ) : null}

            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
              <Section title="Planning parameters">
                <div className="space-y-3 text-sm">
                  <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                    Start date
                    <input
                      className="input"
                      type="date"
                      value={paramsInput.startDate}
                      onChange={(event) => {
                        setParamsInput((current) => ({ ...current, startDate: event.target.value }));
                        setEditDirty(true);
                      }}
                      disabled={!canEdit}
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                    Wave duration (weeks)
                    <input
                      className="input"
                      type="number"
                      min={1}
                      value={paramsInput.waveDurationWeeks}
                      onChange={(event) => {
                        setParamsInput((current) => ({ ...current, waveDurationWeeks: Number(event.target.value) }));
                        setEditDirty(true);
                      }}
                      disabled={!canEdit}
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                    Horizon (months)
                    <input
                      className="input"
                      type="number"
                      min={1}
                      value={paramsInput.horizonMonths}
                      onChange={(event) => {
                        setParamsInput((current) => ({ ...current, horizonMonths: Number(event.target.value) }));
                        setEditDirty(true);
                      }}
                      disabled={!canEdit}
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                    Max parallel apps
                    <input
                      className="input"
                      type="number"
                      min={1}
                      value={paramsInput.maxParallelApps}
                      onChange={(event) => {
                        setParamsInput((current) => ({ ...current, maxParallelApps: Number(event.target.value) }));
                        setEditDirty(true);
                      }}
                      disabled={!canEdit}
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                    Capacity assumptions
                    <input
                      className="input"
                      type="text"
                      placeholder="Ex: 2 squads, 1 architecte, 1 data lead"
                      value={paramsInput.capacityAssumptions ?? ""}
                      onChange={(event) => {
                        setParamsInput((current) => ({ ...current, capacityAssumptions: event.target.value }));
                        setEditDirty(true);
                      }}
                      disabled={!canEdit}
                    />
                  </label>
                </div>
              </Section>

              <Section title="Governance">
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Badge label={stopReason} tone={stopReason === "PASS" ? "success" : "warning"} />
                    {draftSuggestion.status ? <Badge label={draftSuggestion.status} /> : null}
                  </div>
                  {missingInfo.length ? (
                    <div className="space-y-2">
                      {missingInfo.map((info) => (
                        <div key={info.id} className="rounded border border-border px-3 py-2 text-xs">
                          <div className="font-semibold text-foreground">{info.question}</div>
                          <div className="text-muted-foreground">{info.whyItMatters}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground">Aucun blocage détecté.</div>
                  )}
                  <button
                    type="button"
                    onClick={handleSaveEdits}
                    disabled={!canEdit || !editDirty || saveState === "saving"}
                    className="button button-primary"
                  >
                    {saveState === "saving" ? "Enregistrement…" : "Sauvegarder"}
                  </button>
                  {saveState === "saved" ? (
                    <span className="text-xs text-emerald-600">Enregistré.</span>
                  ) : null}
                </div>
              </Section>
            </div>

            <Section title="Wave schedule">
              {waves.length ? (
                <div className="space-y-3">
                  {waves.map((wave) => (
                    <div key={wave.id} className="grid gap-3 rounded-lg border border-border px-4 py-3 md:grid-cols-3">
                      <div>
                        <div className="text-sm font-semibold text-foreground">{wave.name}</div>
                        <div className="text-xs text-muted-foreground">{wave.id}</div>
                      </div>
                      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                        Start
                        <input
                          className="input"
                          type="date"
                          value={wave.start}
                          onChange={(event) => handleWaveDateChange(wave.id, "start", event.target.value)}
                          disabled={!canEdit}
                        />
                      </label>
                      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                        End
                        <input
                          className="input"
                          type="date"
                          value={wave.end}
                          onChange={(event) => handleWaveDateChange(wave.id, "end", event.target.value)}
                          disabled={!canEdit}
                        />
                      </label>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-muted-foreground">Aucune vague disponible.</div>
              )}
            </Section>

            <Section title="Applications sequencing">
              {flatApplications.length ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-border text-left text-sm">
                    <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="px-4 py-3">Wave</th>
                        <th className="px-4 py-3">Dates</th>
                        <th className="px-4 py-3">Application</th>
                        <th className="px-4 py-3">Score</th>
                        <th className="px-4 py-3">Dependencies</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {flatApplications.map((app) => (
                        <tr key={app.segmentId} className="hover:bg-muted/30">
                          <td className="px-4 py-3">
                            <SelectInput
                              value={app.waveId}
                              onChange={(event) => handleMoveApplication(app.segmentId, event.target.value)}
                              disabled={!canEdit}
                            >
                              {waves.map((wave) => (
                                <option key={wave.id} value={wave.id}>
                                  {wave.name}
                                </option>
                              ))}
                            </SelectInput>
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">
                            {app.waveStart} → {app.waveEnd}
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm font-semibold text-foreground">{app.name ?? app.segmentId}</div>
                            <div className="text-xs text-muted-foreground">{app.rationale}</div>
                          </td>
                          <td className="px-4 py-3 text-xs font-semibold text-foreground">{formatScore(app.finalScore)}</td>
                          <td className="px-4 py-3">
                            {app.prerequisites.length ? (
                              <div className="flex flex-wrap gap-2">
                                {app.prerequisites.map((dependencyId) => (
                                  <Tag
                                    key={`${app.segmentId}-${dependencyId}`}
                                    label={applicationNameById.get(dependencyId) ?? dependencyId}
                                  />
                                ))}
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">Aucune</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-xs text-muted-foreground">Aucune application séquencée.</div>
              )}
            </Section>

            <div className="grid gap-4 lg:grid-cols-3">
              <Section title="Assumptions">
                {draftSuggestion.suggestionJson.assumptions.length ? (
                  <div className="space-y-2">
                    {draftSuggestion.suggestionJson.assumptions.map((assumption) => (
                      <div key={assumption.id} className="rounded border border-border px-3 py-2 text-xs">
                        <div className="font-semibold text-foreground">{assumption.text}</div>
                        <div className="text-muted-foreground">Impact: {assumption.impact}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground">Aucune hypothèse.</div>
                )}
              </Section>
              <Section title="Constraints">
                {draftSuggestion.suggestionJson.constraints.length ? (
                  <div className="space-y-2">
                    {draftSuggestion.suggestionJson.constraints.map((constraint) => (
                      <div key={constraint.id} className="rounded border border-border px-3 py-2 text-xs">
                        {constraint.text}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground">Aucune contrainte.</div>
                )}
              </Section>
              <Section title="Risks">
                {draftSuggestion.suggestionJson.risks.length ? (
                  <div className="space-y-2">
                    {draftSuggestion.suggestionJson.risks.map((risk) => (
                      <div key={risk.id} className="rounded border border-border px-3 py-2 text-xs">
                        <div className="font-semibold text-foreground">{risk.text}</div>
                        <div className="text-muted-foreground">Mitigation: {risk.mitigation}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground">Aucun risque listé.</div>
                )}
              </Section>
            </div>
          </div>
        )}
      </Pane>
    </div>
  );
}
